// `/api/access` — the cierres access model (BR-34), for the Usuarios screen (BR-37).
//
// The backend takes the caller and the business from the session, never from the body: these
// calls send only the TARGET (a user id, a role id) and what to write. Every answer is
// `{ success, data }`, or `{ success: false, message, errorCode, details? }` with a Spanish message
// meant to be shown as it comes. None is a 401 — `fetchWrapper` would log the user out.
import { backUrl } from "../utils/backURL";
import { fetchWrapper } from "../utils/fetchWrapper";

const postAccess = (token, path, body = {}) =>
  fetchWrapper(`${backUrl}/access/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

// What the signed-in user may do: `mode`, `source`, `permissions`, `scopes` per permission,
// `assignments`, and `clients` (name + office count in scope, or null if the cierres database did
// not answer).
export const getMyAccess = (token) => postAccess(token, "me");

// The catalogue, the five built-in roles as this business has them and its own. Each role carries
// `holders` (people holding it), `modified` and `defaultPermissions` (a built-in one this business
// changed) and `locked` (Administrador).
export const getAccessRoles = (token) => postAccess(token, "roles");

export const createAccessRole = (token, { name, description, permissions }) =>
  postAccess(token, "roles/create", { name, description, permissions });

export const updateAccessRole = (token, roleId, { name, description, permissions }) =>
  postAccess(token, "roles/update", { roleId, name, description, permissions });

// A built-in role's permissions for this business (`roleKey`: supervisor, operador, auditor or
// cliente — Administrador is a 400 `ACCESS_ROLE_LOCKED`). Sending the catalogue's own list puts the
// role back on its defaults. Applies to every holder from their next request.
export const updateBuiltInRole = (token, roleKey, permissions) =>
  postAccess(token, "roles/builtin/update", { roleKey, permissions });

// 409 `ACCESS_ROLE_IN_USE` while anyone holds it.
export const deleteAccessRole = (token, roleId) => postAccess(token, "roles/delete", { roleId });

// The caller (`editable: false`) and everyone they may manage — nobody else.
export const getAccessUsers = (token) => postAccess(token, "users");

// Replaces the user's whole list. `revision` is the one the screen loaded: someone else saving
// first is a 409 `ACCESS_STALE`, never a silent overwrite of their change.
export const setUserAssignments = (token, userId, assignments, revision) =>
  postAccess(token, "users/assignments", { userId, assignments, revision });

// That user's change-log entries, newest first.
export const getUserAccessHistory = (token, userId) => postAccess(token, "users/history", { userId });
